import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Lock, Gem, Crown } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useText } from "../context/ContentContext";

export default function VIP() {
  const { t } = useTranslation();
  const lede = useText("vip_lede", "vip.lede");
  const [form, setForm] = useState({ email: "", name: "" });
  const [status, setStatus] = useState(null); // success | already | error
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus(null);
    setMsg("");
    setLoading(true);
    try {
      const { data } = await api.post("/vip/subscribe", form);
      setStatus(data.already ? "already" : "success");
    } catch (e2) {
      setStatus("error");
      setMsg(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const tiers = [
    { icon: Lock, name: t("vip.t1_name"), desc: t("vip.t1_desc"), access: t("vip.t1_access") },
    { icon: Gem, name: t("vip.t2_name"), desc: t("vip.t2_desc"), access: t("vip.t2_access") },
    { icon: Crown, name: t("vip.t3_name"), desc: t("vip.t3_desc"), access: t("vip.t3_access") },
  ];

  return (
    <div data-testid="vip-page" className="pt-32 md:pt-40 pb-24">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="overline mb-5">{t("vip.overline")}</div>
        <h1 className="font-serif text-5xl md:text-8xl text-[#F5F5F0] leading-[0.9] mb-10">
          {t("vip.title_1")} <br />
          <em className="text-[#D4AF37] not-italic">{t("vip.title_2")}</em>
        </h1>
        <p className="text-[#A3A39D] max-w-2xl mb-20 leading-relaxed text-lg">{lede}</p>

        <div className="mb-24">
          <div className="overline mb-8">{t("vip.tiers_title")}</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tiers.map((tier, i) => {
              const Icon = tier.icon;
              const topAccent = i === 2;
              return (
                <div
                  key={i}
                  data-testid={`vip-tier-${i}`}
                  className={`bg-[#151516] border p-10 flex flex-col ${topAccent ? "border-[#D4AF37]" : "border-white/10"}`}
                >
                  <div className={`w-14 h-14 mb-8 border flex items-center justify-center ${topAccent ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/20 text-[#F5F5F0]"}`}>
                    <Icon strokeWidth={1.25} size={22} />
                  </div>
                  <div className="overline mb-3 text-[10px]">Niveau {i + 1}</div>
                  <h3 className="font-serif text-3xl text-[#F5F5F0] mb-6">{tier.name}</h3>
                  <p className="text-sm text-[#A3A39D] leading-relaxed mb-8 flex-1">{tier.desc}</p>
                  <div className="pt-6 border-t border-white/10 text-xs uppercase tracking-[0.2em] text-[#D4AF37]">
                    {tier.access}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Subscription */}
        <div className="bg-[#151516] border border-white/10 p-10 md:p-16 max-w-3xl mx-auto">
          <div className="overline mb-5">{t("vip.join_title_1")}</div>
          <h2 className="font-serif text-4xl md:text-5xl text-[#F5F5F0] mb-6 leading-tight">
            {t("vip.join_title_1")} <em className="text-[#D4AF37] not-italic">{t("vip.join_title_2")}</em>
          </h2>
          <p className="text-[#A3A39D] mb-10 leading-relaxed">{t("vip.join_lede")}</p>

          {status === "success" ? (
            <div data-testid="vip-success" className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-6 text-[#D4AF37] flex items-start gap-3">
              <Check strokeWidth={1.25} className="shrink-0 mt-0.5" />
              <span className="text-sm leading-relaxed">{t("vip.success")}</span>
            </div>
          ) : status === "already" ? (
            <div data-testid="vip-already" className="border border-white/20 p-6 text-[#F5F5F0] text-sm">
              {t("vip.already")}
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="vip-form">
              <input
                type="text"
                placeholder={t("vip.name_placeholder")}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="lux-input"
                data-testid="vip-name"
              />
              <input
                type="email"
                placeholder={t("vip.email_placeholder")}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="lux-input"
                data-testid="vip-email"
              />
              <div className="md:col-span-2">
                <button type="submit" disabled={loading} data-testid="vip-submit" className="btn-primary disabled:opacity-60">
                  {loading ? t("vip.subscribing") : t("vip.subscribe")}
                </button>
              </div>
              {status === "error" && (
                <div data-testid="vip-error" className="md:col-span-2 text-sm text-red-400 border border-red-900/60 bg-red-900/10 p-3">
                  {msg}
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
